import React from 'react';
import dynamic from 'next/dynamic';
import {MUIBox, MUIGrid, MUITypography} from '@/components/MUI-Components';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {colors, fontFamily, fontSize, fontWeight, borderRadius} from '@/styles/tokens';
import {dateConvertor} from '@/components/Common/dateconverter';
import {formatBlogDate, getBlogUpdatedDate} from '@/util/blogDates';
import {SKELETON_IMAGE} from '@/constants/images';

const DisplayImage = dynamic(() => import('@/components/Ui-components/display-image'));

// Styling migrated from _blog-detail-new.scss into per-element sx constants driven
// by tokens.ts. The SCSS desktop-first @media steps (600/900/1199 → sm/md/lg) are
// expressed as MUI mobile-first breakpoint objects ({xs, sm, md, lg}). The hero
// image targets `& img` because <DisplayImage> (next/image) renders the <img> we
// can't style directly. The category-pill rgba tints come from tokens (primary.tint07
// / primary.tintBorder) — same values the SCSS had.
const sxHeroLeft = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    pr: {xs: 0, md: '20px', lg: '40px'},
    mb: {xs: '24px', md: 0}
} as const;

const sxHeroRight = {
    '& img': {
        borderRadius: {xs: borderRadius.md, sm: borderRadius.lg},
        width: '100%',
        height: 'auto',
        minHeight: {xs: '200px', sm: '260px', md: '360px'},
        objectFit: 'cover'
    }
} as const;

const sxCategoryPill = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    background: colors.primary.light,
    border: `1px solid ${colors.primary.tintBorder}`,
    borderRadius: borderRadius.full,
    padding: '5px 14px',
    mb: {xs: '12px', sm: '18px'},
    width: 'fit-content'
} as const;

const sxCategoryDot = {
    width: '7px',
    height: '7px',
    borderRadius: borderRadius.circle,
    background: colors.primary.main,
    flexShrink: 0
} as const;

const sxCategoryText = {
    fontSize: fontSize[12],
    fontWeight: fontWeight.semibold,
    letterSpacing: '0.08em',
    color: colors.primary.main,
    textTransform: 'uppercase'
} as const;

const sxTitle = {
    fontFamily: fontFamily.heading,
    fontSize: {xs: fontSize[22], sm: fontSize[28], md: fontSize[34], lg: fontSize[42]},
    color: colors.text.primary,
    lineHeight: {xs: 1.2, sm: 1.15},
    mb: '20px'
} as const;

const sxExcerpt = {
    fontFamily: fontFamily.body,
    fontSize: {xs: fontSize[14], sm: fontSize[16]},
    lineHeight: 1.7,
    color: colors.text.primary,
    mb: '20px'
} as const;

const sxDateRow = {
    fontFamily: fontFamily.body,
    fontSize: fontSize[14],
    color: colors.text.muted,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '5px'
} as const;

const sxDateSep = {color: colors.text.muted, margin: '0 2px'} as const;

const sxDateIcon = {fontSize: fontSize[16], color: colors.neutral.gray999} as const;

const BlogHero = ({
    blogCategoryName,
    title,
    excerpt,
    blogData,
    readTime,
    heroImage
}: {
    blogCategoryName: string;
    title: string;
    excerpt: string;
    blogData: any;
    readTime: string;
    heroImage: string;
}) => {
    const updatedDate = getBlogUpdatedDate(blogData);

    return (
        <MUIGrid container alignItems="center" spacing={4}>
            <MUIGrid item xs={12} md={6} lg={5} sx={sxHeroLeft}>
                {/* Category pill */}
                {blogCategoryName && (
                    <MUIBox sx={sxCategoryPill}>
                        <MUIBox component="span" sx={sxCategoryDot} />
                        <MUIBox component="span" sx={sxCategoryText}>
                            {blogCategoryName}
                        </MUIBox>
                    </MUIBox>
                )}

                {/* Title */}
                <MUITypography sx={sxTitle} component="h1">
                    {title}
                </MUITypography>

                {/* Excerpt */}
                {excerpt && <MUITypography sx={sxExcerpt}>{excerpt}</MUITypography>}

                {/* Date row */}
                <MUIBox sx={sxDateRow}>
                    <AccessTimeIcon sx={sxDateIcon} />
                    <span>Published {blogData ? dateConvertor(blogData.date) : '—'}</span>
                    {updatedDate && (
                        <>
                            <MUIBox component="span" sx={sxDateSep}>
                                ·
                            </MUIBox>
                            <span>Updated {formatBlogDate(updatedDate)}</span>
                        </>
                    )}
                    <MUIBox component="span" sx={sxDateSep}>
                        ·
                    </MUIBox>
                    <span>{readTime}</span>
                </MUIBox>
            </MUIGrid>

            <MUIGrid item xs={12} md={6} lg={7} sx={sxHeroRight}>
                <DisplayImage
                    src={heroImage || SKELETON_IMAGE}
                    height={480}
                    width={600}
                    alt={title}
                    priority
                    // Hero column is full-width on mobile, ~58% (md 6 / lg 7) on desktop — match so
                    // next/image serves a large-enough variant instead of the default 33vw (blurry).
                    sizes="(max-width: 900px) 100vw, 60vw"
                />
            </MUIGrid>
        </MUIGrid>
    );
};

export default BlogHero;
